import React, { useState } from 'react';

// --- Icons (Simulated Lucide React) ---
const DatabaseIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M21 19c0 1.66-4 3-9 3s-9-1.34-9-3"/></svg>;
const ClockIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const UserCheckIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><polyline points="19 11 22 14 19 17"/></svg>;
const SaveIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const AlertCircleIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;


// --- Constants & Initial Data ---
const PRIMARY_COLOR = 'indigo-600';
const SECONDARY_COLOR = 'purple-600';

const INITIAL_SETTINGS = {
    retentionEnabled: true,
    retentionPeriod: 36, // Months
    dataRetentionAction: 'ANONYMIZE', // or 'DELETE'
    candidateAccessEnabled: true,
    accessContactEmail: 'hr-compliance@yourcompany.com',
    purgeLastRun: '2023-11-01',
    nextPurgeScheduled: '2024-11-01',
};

/**
 * Main Candidate Database Control Component
 */
const CandidateDatabaseControl = () => {
    const [settings, setSettings] = useState(INITIAL_SETTINGS);
    const [isSaving, setIsSaving] = useState(false);
    
    // Simple mock save function
    const handleSave = () => {
        setIsSaving(true);
        console.log('Saving Settings:', settings);
        setTimeout(() => {
            setIsSaving(false);
            alert('Settings saved successfully!');
        }, 1500);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (name === 'retentionPeriod' ? Number(value) : value),
        }));
    };

    const StatusCard = ({ title, value, icon: Icon, colorClass }) => (
        <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
                <p className="text-sm font-poppins font-medium text-gray-500">{title}</p>
                <p className={`text-xl font-poppins font-semibold ${colorClass}`}>{value}</p>
            </div>
            <Icon className={`w-6 h-6 ${colorClass}`} />
        </div>
    );

    const RetentionSection = () => (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-lg space-y-5">
            <h3 className="text-xl font-poppins font-semibold text-gray-800 flex items-center">
                <ClockIcon className={`w-5 h-5 mr-2 text-${SECONDARY_COLOR}`} />
                Data Retention & Purge Policy
            </h3>
            <p className="text-sm text-gray-500 border-b pb-4">
                Define the policy for deleting or anonymizing data from candidates who were rejected or are no longer active in the pipeline, ensuring **compliance with privacy regulations**.
            </p>

            {/* Retention Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                <label htmlFor="retentionEnabled" className="text-sm font-medium text-gray-800">
                    Enable Automatic Data Retention Policy
                </label>
                <input
                    id="retentionEnabled"
                    name="retentionEnabled"
                    type="checkbox"
                    checked={settings.retentionEnabled}
                    onChange={handleChange}
                    className={`h-5 w-5 rounded focus:ring-${PRIMARY_COLOR} text-${PRIMARY_COLOR} border-gray-300`}
                />
            </div>

            {settings.retentionEnabled && (
                <div className="space-y-4 pt-2">
                    {/* Retention Period */}
                    <div>
                        <label htmlFor="retentionPeriod" className="advanced-label">
                            Data Retention Period (Months) *
                        </label>
                        <input
                            id="retentionPeriod"
                            name="retentionPeriod"
                            type="number"
                            min="12"
                            max="60"
                            step="1"
                            value={settings.retentionPeriod}
                            onChange={handleChange}
                            required
                            className="advanced-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Data for inactive candidates will be purged after **{settings.retentionPeriod} months** (Recommended: 36 months).
                        </p>
                    </div>

                    {/* Retention Action */}
                    <div>
                        <label htmlFor="dataRetentionAction" className="advanced-label">
                            Purge Action *
                        </label>
                        <select
                            id="dataRetentionAction"
                            name="dataRetentionAction"
                            value={settings.dataRetentionAction}
                            onChange={handleChange}
                            required
                            className="advanced-input"
                        >
                            <option value="ANONYMIZE">ANONYMIZE (Keep application stats, remove PII)</option>
                            <option value="DELETE">DELETE (Permanently remove all records)</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Anonymization is recommended for historical reporting accuracy.
                        </p>
                    </div>

                    {/* Purge Status */}
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-lg flex items-start space-x-3">
                        <AlertCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium">Compliance Warning</p>
                            <p className="text-sm">
                                Last Purge Run: <span className="font-semibold">{settings.purgeLastRun}</span>.
                                Next Purge Scheduled: <span className="font-semibold">{settings.nextPurgeScheduled}</span>.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const AccessSection = () => (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-lg space-y-5">
            <h3 className="text-xl font-poppins font-semibold text-gray-800 flex items-center">
                <UserCheckIcon className={`w-5 h-5 mr-2 text-${PRIMARY_COLOR}`} />
                Candidate Data Access & Opt-Out
            </h3>
            <p className="text-sm text-gray-500 border-b pb-4">
                Manage how candidates can exercise their **Right to Access** and **Right to Be Forgotten**.
            </p>
            
            {/* Candidate Access Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                <label htmlFor="candidateAccessEnabled" className="text-sm font-medium text-gray-800">
                    Enable Candidate Self-Service Data Access Request
                </label>
                <input
                    id="candidateAccessEnabled"
                    name="candidateAccessEnabled"
                    type="checkbox"
                    checked={settings.candidateAccessEnabled}
                    onChange={handleChange}
                    className={`h-5 w-5 rounded focus:ring-${PRIMARY_COLOR} text-${PRIMARY_COLOR} border-gray-300`}
                />
            </div>
            
            {/* Contact Email */}
            <div>
                <label htmlFor="accessContactEmail" className="advanced-label">
                    Compliance Contact Email for Data Requests *
                </label>
                <input
                    id="accessContactEmail"
                    name="accessContactEmail"
                    type="email"
                    value={settings.accessContactEmail}
                    onChange={handleChange}
                    required
                    className="advanced-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                    This email is used in compliance notices for candidates to submit formal data removal or access requests.
                </p>
            </div>
            
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-6 sm:p-8 font-roboto">
            {/* Global Styles for HRMS Look and Focus Fix */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@400;500;700&display=swap');
                    .font-poppins { font-family: 'Poppins', sans-serif; }
                    .font-roboto { font-family: 'Roboto', sans-serif; }
                    /* Custom colors matching the visual style */
                    .bg-indigo-600 { background-color: #4f46e5; }
                    .hover\\:bg-indigo-700:hover { background-color: #4338ca; }
                    .text-indigo-600 { color: #4f46e5; }
                    .ring-indigo-600 { --tw-ring-color: #4f46e5; }

                    .bg-purple-600 { background-color: #9333ea; }
                    .hover\\:bg-purple-700:hover { background-color: #7e22ce; }
                    .text-purple-600 { color: #9333ea; }

                    /* --- GLOBAL FOCUS FIX --- */
                    *:focus { outline: none !important; }
                    
                    .advanced-input:focus-visible,
                    button:focus-visible,
                    select:focus-visible {
                        outline: 2px solid transparent;
                        outline-offset: 2px;
                        border-color: #6366f1; /* indigo-500 */
                        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.4) !important; /* Custom focus ring */
                    }
                    
                    /* Advanced Input/Label Styles */
                    .advanced-label {
                        display: block;
                        font-size: 0.875rem; /* text-sm */
                        font-weight: 500; /* font-medium */
                        color: #374151; /* text-gray-700 */
                        margin-bottom: 0.25rem;
                        font-family: 'Roboto', sans-serif;
                    }
                    .advanced-input {
                        width: 100%;
                        padding: 0.65rem 0.75rem;
                        border: 1px solid #d1d5db;
                        border-radius: 0.5rem;
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                        font-size: 0.875rem;
                        font-family: 'Roboto', sans-serif;
                        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                        -webkit-appearance: none;
                        -moz-appearance: none;
                        appearance: none;
                    }
                `}
            </style>

            <div className="max-w-4xl w-full bg-white rounded-xl shadow-2xl border border-gray-100 mt-4 overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-white flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-poppins font-semibold text-gray-800 flex items-center">
                            <DatabaseIcon className={`w-7 h-7 mr-3 text-${PRIMARY_COLOR}`} />
                            Candidate Database Control
                        </h1>
                        <p className="text-sm text-gray-500 mt-1 font-roboto">
                            Manage data retention, privacy, and compliance settings for all candidate records.
                        </p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="p-6 space-y-8">
                    {/* Status Overview Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <StatusCard 
                            title="Retention Policy" 
                            value={settings.retentionEnabled ? "Active" : "Disabled"}
                            icon={ClockIcon}
                            colorClass={settings.retentionEnabled ? "text-green-600" : "text-red-500"}
                        />
                         <StatusCard 
                            title="Retention Period" 
                            value={`${settings.retentionPeriod} Months`}
                            icon={DatabaseIcon}
                            colorClass={`text-${SECONDARY_COLOR}`}
                        />
                         <StatusCard 
                            title="Candidate Access" 
                            value={settings.candidateAccessEnabled ? "Enabled" : "Disabled"}
                            icon={UserCheckIcon}
                            colorClass={settings.candidateAccessEnabled ? `text-${PRIMARY_COLOR}` : "text-gray-500"}
                        />
                    </div>

                    {/* Configuration Sections */}
                    <RetentionSection />
                    <AccessSection />
                </div>
                
                {/* Footer/Action Bar */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={handleSave}
                        className={`px-6 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md flex items-center space-x-2 transition 
                            bg-${PRIMARY_COLOR} hover:bg-indigo-700 disabled:bg-gray-400
                        `}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        ) : (
                            <SaveIcon className="w-4 h-4" />
                        )}
                        <span>{isSaving ? 'Saving...' : 'Save All Settings'}</span>
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CandidateDatabaseControl;